import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import Swal from 'sweetalert2';
import {BlogService} from '../../../../shared/services/blog.service';
import {BlogCategoryService} from '../../../../shared/services/blog-category.service';
import {environment} from '../../../../../environments/environment';

@Component({
    selector: 'app-blog-list',
    templateUrl: './blog-list.component.html',
    styleUrls: ['./blog-list.component.scss']
})
export class BlogListComponent implements OnInit, OnDestroy {
    blogs: any[] = [];
    categories: any[] = [];
    loading = false;
    searchForm: FormGroup;
    pagination = {page: 1, total: 0, perpage: 12, pages: 0};
    backendUrl = environment.appUrl;

    private destroy$ = new Subject<void>();

    constructor(private fb: FormBuilder,
                private router: Router,
                private blogService: BlogService,
                private categoryService: BlogCategoryService) {
    }

    ngOnInit(): void {
        this.searchForm = this.fb.group({
            title: [''],
            status: [''],
            category_id: ['']
        });

        this.searchForm.valueChanges
            .pipe(debounceTime(350), takeUntil(this.destroy$))
            .subscribe(() => {
                this.pagination.page = 1;
                this.fetch();
            });

        this.loadCategories();
        this.fetch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadCategories(): void {
        this.categoryService.list({page: -1}).pipe(takeUntil(this.destroy$)).subscribe(res => {
            this.categories = (res && res.data) ? res.data : [];
        });
    }

    fetch(): void {
        const params: any = {
            page: this.pagination.page,
            per_page: this.pagination.perpage,
            ...this.cleanSearchValues()
        };
        this.loading = true;
        this.blogs = [];
        this.blogService.list(params).pipe(takeUntil(this.destroy$)).subscribe(res => {
            this.loading = false;
            if (res) {
                this.blogs = res.data || [];
                this.pagination = {
                    page: res.current_page,
                    total: res.total,
                    perpage: res.per_page,
                    pages: res.last_page
                };
            }
        }, () => {
            this.loading = false;
        });
    }

    cleanSearchValues(): any {
        const v = this.searchForm.value;
        const out: any = {};
        Object.keys(v).forEach(k => {
            if (v[k] !== '' && v[k] !== null && v[k] !== undefined) {
                out[k] = v[k];
            }
        });
        return out;
    }

    onPageChange(page: number): void {
        this.pagination.page = page;
        this.fetch();
    }

    create(): void {
        this.router.navigate(['/blogs/new']);
    }

    edit(blog: any): void {
        this.router.navigate(['/blogs', blog.id, 'edit']);
    }

    remove(blog: any): void {
        Swal.fire({
            title: 'Delete this post?',
            text: blog.title,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#ea5455',
            customClass: {confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1'},
            buttonsStyling: false
        }).then(result => {
            if (result.isConfirmed) {
                this.blogService.remove(blog.id).pipe(takeUntil(this.destroy$)).subscribe(() => this.fetch());
            }
        });
    }

    coverUrl(blog: any): string {
        if (!blog?.cover_image) {
            return '';
        }
        if (blog.cover_image.startsWith('http')) {
            return blog.cover_image;
        }
        return this.backendUrl + blog.cover_image;
    }

    resetFilters(): void {
        this.searchForm.reset({title: '', status: '', category_id: ''});
    }
}
